/**
 * FormModal — the shared Arbi modal shell (Day 98f Part 1).
 *
 * The codebase's FIRST shared modal primitive, built to COMPONENT_STANDARDS §5:
 * surface-0/80 backdrop, surface-1 + rounded-xl + shadow-xl + max-w-md card,
 * header (title left + X close right), body (children), footer (action slot),
 * focus-trap, Escape + click-outside close (BOTH suppressed while `saving` or
 * while a nested ConfirmDialog is open), focus returns to the trigger on close,
 * and a 150ms fade + scale entrance. Section-agnostic — sections supply the
 * children, footer, and open/close state; the shell branches on nothing
 * per-section (an edit-only section just never renders an add trigger).
 *
 * Note: §5 names the card shadow "shadow-strong", which is not a wired Tailwind
 * class (no boxShadow token / CSS var). The §5-correct sibling modals
 * (FulfillmentOrders / FinanceInvoices) render `shadow-xl`; FormModal follows
 * that de-facto convention. The shadow-strong doc-token drift is forward-filed.
 */
import { useEffect, useRef, useState, type ReactNode, type JSX } from 'react';
import { X } from 'lucide-react';

type FormModalSize = 'md' | 'lg' | 'xl' | '2xl' | '3xl';

const SIZE_CLASS: Record<FormModalSize, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
};

// Focusable descendants for the focus-trap + initial focus (excludes
// tabindex="-1", e.g. the backdrop button).
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface FormModalProps {
  /** Controls render — `false` returns null (no exit animation per §5). */
  open: boolean;
  /** Header title (left) + default aria-label. */
  title: string;
  /** Optional sub-line under the title. */
  subtitle?: ReactNode;
  /** Overrides the dialog aria-label (defaults to `title`). */
  ariaLabel?: string;
  /** Max width; `md` (default) per §5, opt wider for denser forms. */
  size?: FormModalSize;
  /** The form body. */
  children: ReactNode;
  /** Action slot — sections supply Cancel (left) + primary (right) per §5. */
  footer: ReactNode;
  /** Suppresses Escape + click-outside + X while a save is in flight. */
  saving?: boolean;
  /** Suppresses Escape + click-outside + X while a nested ConfirmDialog is open. */
  confirmOpen?: boolean;
  /** Close handler — fired by Escape, click-outside, and the X (unless locked). */
  onClose: () => void;
}

/**
 * Shared §5 modal shell. See file header for the full contract.
 */
export function FormModal({
  open,
  title,
  subtitle,
  ariaLabel,
  size = 'md',
  children,
  footer,
  saving = false,
  confirmOpen = false,
  onClose,
}: FormModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [entered, setEntered] = useState<boolean>(false);

  // Both close affordances are suppressed mid-write and while a confirm sits on top.
  const locked = saving || confirmOpen;

  // Entrance: flip the flag after the first paint so the fade + scale transition fires.
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Focus management: stash the trigger, move focus to the first body field on
  // open, return focus to the trigger on close (§5 / UI_STANDARDS §23).
  useEffect(() => {
    if (!open) return undefined;
    triggerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const id = requestAnimationFrame(() => {
      const firstField = bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      (firstField ?? dialogRef.current)?.focus();
    });
    return () => {
      cancelAnimationFrame(id);
      triggerRef.current?.focus?.();
    };
  }, [open]);

  // Escape + Tab focus-trap via one window-level keydown listener (mirrors
  // OverrideEditModal). Escape is suppressed while locked; Tab cycles within the dialog.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (!locked) onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const el = dialogRef.current;
      if (!el) return;
      const nodes = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, locked, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[var(--z-modal,200)] flex items-center justify-center">
      {/* Backdrop — click-outside close; keyboard dismiss handled by the Escape listener. */}
      <button
        type="button"
        aria-label={`Close ${title}`}
        tabIndex={-1}
        onClick={locked ? undefined : onClose}
        className={`absolute inset-0 cursor-default bg-surface-0/80 transition-opacity duration-150 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        className={`relative mx-4 flex max-h-[85vh] w-full ${SIZE_CLASS[size]} flex-col rounded-xl border border-border bg-surface-1 shadow-xl transition duration-150 ease-out ${
          entered ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Header — title left, X close right (§5). */}
        <div className="flex items-start justify-between gap-3 border-b border-border-subtle px-5 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-bright">{title}</h3>
            {subtitle != null && <p className="mt-0.5 text-2xs text-ink-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={locked ? undefined : onClose}
            disabled={locked}
            aria-label="Close"
            className="arbi-btn-ghost -mr-1 rounded p-1 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — the form. */}
        <div ref={bodyRef} className="space-y-4 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {/* Footer — action slot (cancel left of primary, both right-aligned per §5). */}
        <div className="flex justify-end gap-2 border-t border-border-subtle px-5 py-3">
          {footer}
        </div>
      </div>
    </div>
  );
}
